// =============================================================
// OS-level (desktop) notifications — page-driven
// =============================================================
// Shows native operating-system notifications (Windows Action
// Center, macOS Notification Center, etc.) for in-app events while
// the app tab is open.
//
// This complements pushManager.ts / sw.js (Web Push), which delivers
// notifications even when the tab is CLOSED but requires VAPID keys
// and a backend push library to be configured. When Web Push is NOT
// configured (the common case), this module is what actually surfaces
// notifications to the OS.
//
// To avoid double notifications, showOsNotification() bails out if an
// active Web Push subscription exists — in that case the Service
// Worker already handles delivery.
// =============================================================

// Matches the icon used by the Service Worker push handler (sw.js).
const ICON = "/src/favicon.svg";

/**
 * Ask the user for notification permission (once).
 * Safe to call repeatedly; returns the current permission state.
 */
export async function ensureNotificationPermission(): Promise<NotificationPermission> {
  if (typeof Notification === "undefined") return "denied";
  if (Notification.permission !== "default") return Notification.permission;
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

/**
 * True when an active Web Push subscription exists, meaning the
 * Service Worker will deliver OS notifications for us — so the page
 * should NOT also show one (it would be a duplicate).
 */
async function hasActivePushSubscription(): Promise<boolean> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return false;
  }
  try {
    const reg = await navigator.serviceWorker.getRegistration("/");
    if (!reg) return false;
    const sub = await reg.pushManager.getSubscription();
    return sub !== null;
  } catch {
    return false;
  }
}

export interface OsNotificationInput {
  title: string;
  body: string;
  /** In-app route to open when the notification is clicked. */
  link?: string | null;
  /** Stable tag so repeated notifications collapse instead of stacking. */
  tag?: string;
}

/**
 * Show a single native OS notification (if permitted and not already
 * covered by Web Push). Clicking it focuses the app and navigates to
 * the supplied link.
 */
export async function showOsNotification(input: OsNotificationInput): Promise<void> {
  if (typeof Notification === "undefined") return;
  if (Notification.permission !== "granted") return;

  // Don't duplicate what the Service Worker push already shows.
  if (await hasActivePushSubscription()) return;

  try {
    const notification = new Notification(input.title, {
      body: input.body,
      icon: ICON,
      badge: ICON,
      tag: input.tag,
    });

    notification.onclick = () => {
      window.focus();
      if (input.link) {
        // Navigate the app to the linked route.
        window.location.assign(input.link);
      }
      notification.close();
    };
  } catch {
    // Some browsers throw if Notification is constructed without a SW.
    // Silently ignore — the in-app bell still works.
  }
}
